// MS-6466: Showroom's half of the single-session hand-off. book-api refuses a superseded
// caller with 401 and the programme's contract body; this module decides when that happened
// and performs the one-shot hand-off to the login app's /logout with the reason it localises.
import config from '../../config';

// book-api's system/session_floor.go WriteSessionSuperseded — the contract string shared with
// the 15 CRM readers.
const SUPERSEDED_BODY = 'invalid user session';

// The message decides, not the status alone: CRM learned that a bare status check misclassifies
// gateway/WAF auth failures as takeover and fabricates a "someone signed in on another device"
// explanation the user then acts on (mhub-leads-v2-web src/utils/session.js). And it is an
// EQUALITY check, not a substring one: book-web's only legitimate emitter is book-api's REST
// response — there is no AppSync runtime here to wrap the message, so anything else containing
// the phrase is not the contract and must keep the ordinary 401 handling. Trailing whitespace
// and case are tolerated (an http.Error-style regression appends a newline).
export function isSessionSuperseded(status, body) {
  return Number(status) === 401
    && String(body || '').trim().toLowerCase() === SUPERSEDED_BODY;
}

// One-shot latch: when a session dies, every in-flight fetch fails together and exactly one may
// navigate. The latch flips AFTER the navigation call returns — a throwing navigation leaves it
// open, so the next failing fetch retries the hand-off instead of stranding the user on a dead
// screen (same semantics as CRM's sessionLostHandled).
let sessionLostHandled = false;
// When the hand-off navigation was triggered. The flash suppression below is time-boxed to this,
// NOT to the permanent latch: a full-page /logout navigation unloads well within the window, but
// if it is BLOCKED (a beforeunload unsaved-changes prompt the user cancels), the page survives —
// and error flashes must resume after the window rather than stay silenced for the page's life.
let handoffAt = 0;
const HANDOFF_FLASH_WINDOW_MS = 5000;
const nowMs = () => Date.now();

export function handleSessionLost(go) {
  if (sessionLostHandled) {
    return;
  }
  const navigate = go || ((url) => { window.location.assign(url); });
  // /logout, deliberately not /login: the id token is still VALID — only the session floor
  // refuses it — so /login would bounce straight back into the app and loop. The login app
  // localises the reason (logout.reason.sessionTakenOver, all four locales).
  navigate(`${config.get('startURL')}/logout?reason=session-taken-over`);
  sessionLostHandled = true;
  handoffAt = nowMs();
}

// True while a takeover hand-off is navigating away (bounded window). A superseded session 401s
// every in-flight request at once, and each caller's own catch would flash "An error occurred,
// please refresh page" — noise on top of a page already leaving for /logout. Error flashes check
// this and stay silent during that window; if the navigation was cancelled they resume after it.
export function isSessionHandoffInProgress() {
  return sessionLostHandled && (nowMs() - handoffAt) < HANDOFF_FLASH_WINDOW_MS;
}

export function resetSessionLostLatchForTests() {
  sessionLostHandled = false;
  handoffAt = 0;
}



// WEBPACK FOOTER //
// ./src/utils/session.js